"use client"

import * as React from "react"
import { Toaster as Sonner, ToasterProps } from "sonner"
import { Z_INDEX } from "../lib/utils"

/**
 * Resolve the active theme from the `.dark` class on <html> rather than
 * next-themes. The apps here manage theme with their OWN provider (which
 * toggles that class + persists to localStorage) and do NOT mount a
 * next-themes provider — so `useTheme()` from next-themes was always
 * undefined → "system", making toasts follow the OS color scheme instead of
 * the app's actual light/dark. A MutationObserver keeps it in sync on toggle.
 */
function useHtmlTheme(): "light" | "dark" {
  const [theme, setTheme] = React.useState<"light" | "dark">("light")
  React.useEffect(() => {
    const el = document.documentElement
    const read = () => setTheme(el.classList.contains("dark") ? "dark" : "light")
    read()
    const observer = new MutationObserver(read)
    observer.observe(el, { attributes: true, attributeFilter: ["class"] })
    return () => observer.disconnect()
  }, [])
  return theme
}

const Toaster = ({ ...props }: ToasterProps) => {
  const theme = useHtmlTheme()

  return (
    <Sonner
      theme={theme}
      position="top-center"
      expand={true}
      richColors={true}
      closeButton={true}
      className="toaster group"
      // Z_INDEX.* are numbers, so set z-index via inline style — interpolating
      // it into className produced an invalid class and dropped the z-index.
      // Keep it above the modal (1050) so toasts show over a fullscreen dialog.
      style={{ zIndex: Z_INDEX.toast }}
      toastOptions={{
        style: {
          position: 'relative',
          // Radix modals set `body { pointer-events: none }` while open, and
          // the toaster is portaled into <body> — without this the toast (and
          // its close/action buttons) render but aren't clickable.
          pointerEvents: 'auto',
        },
        classNames: {
          toast: "toast border shadow-lg",
          title: "text-sm font-semibold",
          description: "text-xs opacity-90",
          actionButton: "bg-primary text-primary-foreground hover:bg-primary/90",
          cancelButton: "bg-muted text-muted-foreground hover:bg-muted/90",
          closeButton: "bg-background border hover:bg-muted",
          error: "border-red-500 text-red-900 bg-red-50 dark:bg-red-900/20 dark:text-red-100",
          success: "border-green-500 text-green-900 bg-green-50 dark:bg-green-900/20 dark:text-green-100",
          warning: "border-orange-500 text-orange-900 bg-orange-50 dark:bg-orange-900/20 dark:text-orange-100",
          info: "border-blue-500 text-blue-900 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-100",
        },
        duration: 5000, // Default 5 seconds
      }}
      {...props}
    />
  )
}

export { Toaster }
